import {apiClient, queryClient} from '@common/http/query-client';
import {staticPagesBaseKey} from '@common/static-pages/use-static-page';
import type {StaticPageDefinition} from '@common/static-pages/static-page-definitions';
import {queryOptions} from '@tanstack/react-query';
import type {TranslatableContent} from '@ui/i18n/translatable-content';

export interface SystemPageSummary {
  key: string;
  name: string;
  path: string;
  fields: number;
  changed: number;
  languages: Record<string, {translated: number; total: number}>;
  updated_at: string | null;
}

export interface SystemPageContent extends TranslatableContent {
  customized: boolean;
  updated_at: string | null;
}

const adminKey = ['admin', 'static-pages'];

export const systemPagesOptions = () =>
  queryOptions({
    queryKey: adminKey,
    queryFn: () =>
      apiClient
        .get<{pages: SystemPageSummary[]}>('admin/static-pages')
        .then(r => r.data.pages),
  });

export const systemPageOptions = (key: string) =>
  queryOptions({
    queryKey: [...adminKey, key],
    queryFn: () =>
      apiClient
        .get<{
          page: StaticPageDefinition;
          content: SystemPageContent;
          base_locale: string;
        }>(`admin/static-pages/${key}`)
        .then(r => r.data),
    staleTime: 0,
  });

export function saveSystemPage(key: string, content: TranslatableContent) {
  return apiClient
    .put<{content: SystemPageContent}>(`admin/static-pages/${key}`, content)
    .then(r => r.data.content);
}

export function resetSystemPage(key: string) {
  return apiClient
    .post<{content: SystemPageContent}>(`admin/static-pages/${key}/reset`)
    .then(r => r.data.content);
}

/** After save/reset: refresh admin lists and the public page text. */
export async function afterSystemPageChanged(key: string) {
  await Promise.all([
    queryClient.invalidateQueries({queryKey: adminKey}),
    queryClient.invalidateQueries({queryKey: [...staticPagesBaseKey, key]}),
  ]);
}
